/**
 * Pipeline Home V2 - Enhanced Pipeline View with Modern UX
 *
 * Features from mockup:
 * - Gradient header with session status
 * - Priority counters as clickable cards with animations
 * - Pending emails banner
 * - Domain-grouped work items with collapsible sections
 * - Rich action buttons on each work item
 * - Enhanced Morning Brief modal
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Play,
  StopCircle,
  RefreshCw,
  ArrowRight,
  Clock,
  AlertTriangle,
  ListChecks,
  LayoutDashboard,
  FileText,
  X,
  Printer,
  Car,
  AlertCircle,
  CheckCircle,
  Check,
  Info,
  Target,
  Eye,
  Mail,
  Edit,
  Upload,
  Phone,
  ChevronDown,
  ChevronRight,
  Sun,
  Loader2,
  Search,
  XCircle,
  Gauge,
  Calendar,
  UserX,
  UserPlus,
  Users,
  User,
  ClipboardCheck,
  Shield,
  Send,
  Folder,
  Wrench,
  Clipboard,
  Hash,
  CreditCard,
  Euro,
} from 'lucide-react';
import { usePipeline } from '../../v3/context/PipelineContext';
import { pipelineAPI } from '../../api/pipeline.js';
import { claimAPI } from '../../api/claims.js';
import { API_BASE_URL, getAuthHeaders } from '../../api/core.js';
import { useToast } from '../common/Toast';
import {
  PIPELINE_DOMAINS,
  PRIORITY_COLORS,
  PRIORITY_BG_COLORS,
  groupItemsByDomain,
  getDomainsWithP0,
  getTypeConfig,
} from './pipelineConfig';
import WorkItemActionModal from './WorkItemActionModal';
import WorkItemFlatList from './WorkItemFlatList';
import WorkItemTimeline from './WorkItemTimeline';
import PendingEmailsAccordion from './PendingEmailsAccordion';
import PipelineQuickEditModal from './PipelineQuickEditModal';
import DriverAvailabilityModal from '../drivers/modals/DriverAvailabilityModal';
import VehicleAssignmentToDriverModal from '../drivers/modals/VehicleAssignmentToDriverModal';
import { driverAPI } from '../../api/drivers.js';
import { vehicleAPI } from '../../api/vehicles.js';
import { useFormat } from '../../i18n/format.js';

// ============================================================================
// PRIORITY COUNTERS V2 - Clickable cards with animations
// ============================================================================

function PriorityCountersV2({ counts, onFilterByPriority, activeFilter }) {
  const counters = [
    {
      key: 'P0',
      label: 'Critique',
      sublabel: 'P0',
      value: counts?.p0 || 0,
      textColor: 'text-red-600',
    },
    {
      key: 'P1',
      label: 'Important',
      sublabel: 'P1',
      value: counts?.p1 || 0,
      textColor: 'text-amber-600',
    },
    {
      key: 'P2',
      label: 'Normal',
      sublabel: 'P2',
      value: counts?.p2 || 0,
      textColor: 'text-blue-600',
    },
    {
      key: 'completed',
      label: 'Termine',
      sublabel: 'Fait',
      value: counts?.completed || 0,
      textColor: 'text-emerald-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {counters.map(counter => {
        const isActive = activeFilter === counter.key;

        return (
          <button
            key={counter.key}
            onClick={() => onFilterByPriority?.(isActive ? null : counter.key)}
            className={`bg-white rounded-lg border border-slate-200 p-3
                       hover:bg-slate-50 transition-colors cursor-pointer text-left
                       ${isActive ? 'ring-2 ring-slate-400 ring-offset-1' : ''}`}
          >
            <p className="text-sm text-slate-500 font-medium">{counter.label}</p>
            <p className={`text-2xl font-bold ${counter.textColor} mt-0.5`}>{counter.value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{counter.sublabel}</p>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// PENDING EMAILS BANNER
// ============================================================================

function PendingEmailsBanner({ items, onViewEmails }) {
  // Count items that have email-related suggested actions or pending email status
  const pendingEmailCount = useMemo(() => {
    return items.filter(item => {
      // Check if item has email_generate suggested action
      if (item.suggested_actions) {
        try {
          const actions =
            typeof item.suggested_actions === 'string'
              ? JSON.parse(item.suggested_actions)
              : item.suggested_actions;
          return actions.some(a => a.action === 'email_generate');
        } catch {
          return false;
        }
      }
      return false;
    }).length;
  }, [items]);

  if (pendingEmailCount === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-slate-400" />
          <div>
            <span className="font-medium text-slate-700 text-sm">
              {pendingEmailCount} email{pendingEmailCount > 1 ? 's' : ''} en attente d'envoi
            </span>
            <span className="text-sm text-slate-500 ml-2">— prets a etre envoyes via Outlook</span>
          </div>
        </div>
        <button
          onClick={onViewEmails}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium transition flex items-center gap-1"
        >
          Voir les emails
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// DOMAIN WORK ITEMS - Collapsible sections
// ============================================================================

function DomainSection({
  domain,
  domainKey,
  items,
  isExpanded,
  onToggle,
  onAction,
  onNavigate,
  onEmail,
  onLogAction,
  onSuggestedAction,
}) {
  const domainConfig = PIPELINE_DOMAINS[domainKey] || PIPELINE_DOMAINS.other;
  const DomainIcon = domainConfig.icon;
  const totalCount = items.length;
  const p0Count = items.filter(i => i.priority === 'P0').length;
  const p1Count = items.filter(i => i.priority === 'P1').length;

  if (totalCount === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-2">
      {/* Domain Header */}
      <button
        onClick={onToggle}
        className="w-full bg-white px-4 py-3 border-b border-slate-200
                   flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <DomainIcon className="w-5 h-5 text-slate-400" />
          <div className="text-left">
            <h3 className="font-medium text-slate-800">{domainConfig.label}</h3>
            <p className="text-sm text-slate-500">
              {totalCount} tache{totalCount > 1 ? 's' : ''}
              {p0Count > 0 && ` dont ${p0Count} critique${p0Count > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {p0Count > 0 && (
            <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded">
              {p0Count} P0
            </span>
          )}
          {p1Count > 0 && (
            <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded">
              {p1Count} P1
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>

      {/* Work Items */}
      {isExpanded && (
        <div className="divide-y divide-slate-100">
          {items.map(item => (
            <WorkItemRowV2
              key={item.id}
              item={item}
              onAction={onAction}
              onNavigate={onNavigate}
              onEmail={onEmail}
              onLogAction={onLogAction}
              onSuggestedAction={onSuggestedAction}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// WORK ITEM ROW V2 - Rich action buttons
// ============================================================================

const ACTION_ICONS = {
  // PascalCase (legacy)
  Edit: Edit, Mail: Mail, Upload: Upload, Eye: Eye, Phone: Phone,
  ArrowRight: ArrowRight, UserX: UserX, UserPlus: UserPlus, FileText: FileText,
  AlertCircle: AlertCircle, RefreshCw: RefreshCw, CheckCircle: CheckCircle,
  Gauge: Gauge, Calendar: Calendar,
  // lowercase (from pipeline rule engine)
  edit: Edit, mail: Mail, upload: Upload, eye: Eye, phone: Phone,
  send: Send, car: Car, shield: Shield, calendar: Calendar,
  folder: Folder, wrench: Wrench, clipboard: Clipboard, hash: Hash,
  gauge: Gauge, euro: Euro, user: User, users: Users,
  'user-plus': UserPlus, 'file-text': FileText, 'id-card': CreditCard,
  'arrow-right': ArrowRight, 'alert-circle': AlertCircle,
};

const ACTION_COLORS = {
  navigate: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  email_generate: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  document_upload: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  form_complete: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  workflow_advance: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  assign: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  call: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
};

function WorkItemRowV2({ item, onAction, onNavigate, onEmail, onLogAction, onSuggestedAction }) {
  const { locale } = useFormat();
  const [actionLoading, setActionLoading] = useState(null);

  const typeConfig = getTypeConfig(item.type);
  const TypeIcon = typeConfig.icon || FileText;
  const priorityBg = PRIORITY_BG_COLORS[item.priority] || 'bg-gray-50';
  const priorityBorder =
    {
      P0: 'border-l-red-500',
      P1: 'border-l-amber-500',
      P2: 'border-l-blue-500',
      P3: 'border-l-gray-400',
    }[item.priority] || 'border-l-gray-400';

  // Parse suggested actions
  const suggestedActions = useMemo(() => {
    if (!item.suggested_actions) return [];
    try {
      const parsed =
        typeof item.suggested_actions === 'string'
          ? JSON.parse(item.suggested_actions)
          : item.suggested_actions;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [item.suggested_actions]);

  // Format due date
  const formatDueDate = dateStr => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: `${Math.abs(diffDays)}j en retard`, urgent: true };
    if (diffDays === 0) return { text: "Aujourd'hui", urgent: true };
    if (diffDays === 1) return { text: 'Demain', urgent: false };
    if (diffDays <= 7) return { text: `Dans ${diffDays}j`, urgent: false };
    return {
      text: date.toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
      urgent: false,
    };
  };

  const dueInfo = formatDueDate(item.due_date);

  // Handle suggested action click
  const handleSuggestedAction = async action => {
    setActionLoading(action.action);
    try {
      switch (action.action) {
        case 'navigate':
          if (onNavigate) onNavigate(action.entity || item.entity_kind, action.entityId || item.entity_id);
          break;
        case 'email_generate':
          if (onEmail)
            await onEmail(
              { entity_kind: action.entity, entity_id: action.entityId, id: item.id },
              action.template
            );
          break;
        case 'call':
          if (action.params?.phone) window.location.href = `tel:${action.params.phone}`;
          else if (onNavigate) onNavigate(action.entity, action.entityId);
          break;
        case 'workflow_advance':
          if (onAction) await onAction(item.id, action.workflowAction || 'DONE');
          break;
        case 'form_complete':
        case 'assign':
        case 'document_upload':
          if (onSuggestedAction) onSuggestedAction(item, action);
          else if (onNavigate) onNavigate(action.entity, action.entityId);
          break;
        default:
          if (onNavigate) onNavigate(action.entity, action.entityId);
      }
    } catch (err) {
      console.error('Action failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle done
  const handleDone = async () => {
    setActionLoading('done');
    try {
      if (onAction) await onAction(item.id, 'DONE');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className={`px-4 py-3 border-l-2 ${priorityBorder}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <TypeIcon className="w-4 h-4 text-slate-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-1.5 py-0.5 text-xs font-bold rounded text-white ${PRIORITY_COLORS[item.priority] || 'bg-gray-400'}`}
                >
                  {item.priority}
                </span>
                <h4 className="font-medium text-slate-800">{item.title}</h4>
              </div>
              {item.description && (
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.description}</p>
              )}
              <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <TypeIcon className="w-3 h-3" />
                  {typeConfig.label}
                </span>
                {dueInfo && (
                  <span
                    className={`flex items-center gap-1 ${dueInfo.urgent ? 'text-red-600 font-medium' : ''}`}
                  >
                    <Clock className="w-3 h-3" />
                    {dueInfo.text}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            {/* Suggested Actions */}
            {suggestedActions.map((action, idx) => {
              const ActionIcon = ACTION_ICONS[action.icon] || Eye;
              const colorClass =
                ACTION_COLORS[action.action] || 'bg-gray-100 text-gray-700 hover:bg-gray-200';
              const isLoading = actionLoading === action.action;

              return (
                <button
                  key={idx}
                  onClick={() => handleSuggestedAction(action)}
                  disabled={actionLoading !== null}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${colorClass}
                             disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ActionIcon className="w-4 h-4" />
                  )}
                  {action.label}
                  {action.action === 'call' && action.params?.phone && (
                    <span className="text-xs opacity-80">{action.params.phone}</span>
                  )}
                </button>
              );
            })}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Log Action Button */}
            {onLogAction && (
              <button
                onClick={() => onLogAction(item)}
                disabled={actionLoading !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-md text-sm font-medium
                          hover:bg-slate-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                title="Enregistrer une action effectuée"
              >
                <Edit className="w-4 h-4" />
                Action
              </button>
            )}

            {/* Done Button */}
            <button
              onClick={handleDone}
              disabled={actionLoading !== null}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-md text-sm font-medium
                        hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading === 'done' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Terminé
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MORNING BRIEF MODAL V2 - Enhanced design
// ============================================================================

function MorningBriefModalV2({
  isOpen,
  onClose,
  brief,
  onNavigate,
  onNavigateToEntity,
  onEmail,
  onMarkEntityHandled,
}) {
  const { locale } = useFormat();
  const [handlingEntity, setHandlingEntity] = useState(null);

  if (!isOpen || !brief) return null;

  const handleMarkHandled = async (entity, category) => {
    if (!onMarkEntityHandled) return;
    const key = `${entity.entity}-${entity.entityId}`;
    setHandlingEntity(key);
    try {
      await onMarkEntityHandled(entity, category);
    } finally {
      setHandlingEntity(null);
    }
  };

  const handleActionClick = async action => {
    switch (action.action) {
      case 'navigate':
        onClose();
        if (onNavigate && action.entity) {
          const viewMap = {
            vehicle: 'vehicles',
            driver: 'drivers',
            claim: 'claims',
            maintenance: 'maintenance',
            insurance: 'insurance',
          };
          const view = viewMap[action.entity] || action.route?.replace('/', '');
          if (view) onNavigate(view);
        }
        break;
      case 'navigate_entity':
        // Deep link to specific entity with spotlight
        onClose();
        if (onNavigateToEntity && action.entityId) {
          onNavigateToEntity(action.entity, action.entityId, action.spotlight);
        }
        break;
      case 'email_generate':
        if (onEmail && action.template) await onEmail(null, action.template);
        break;
      default:
        console.warn('Unknown brief action:', action.action);
    }
  };

  const getPriorityStyle = priority => {
    switch (priority) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'bg-red-100',
          iconColor: 'text-red-600',
          text: 'text-red-800',
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          icon: 'bg-amber-100',
          iconColor: 'text-amber-600',
          text: 'text-amber-800',
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'bg-blue-100',
          iconColor: 'text-blue-600',
          text: 'text-blue-800',
        };
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sun className="w-6 h-6 text-slate-400" />
              <div>
                <h2 className="text-xl font-bold text-slate-800">Morning Brief</h2>
                <p className="text-sm text-slate-500">
                  {new Date(brief.generated_at).toLocaleDateString(locale, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.print()}
                className="p-2 hover:bg-slate-100 rounded-md transition text-slate-400 hover:text-slate-600"
                title="Imprimer"
              >
                <Printer className="w-5 h-5" />
              </button>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md transition text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          {/* Fleet Overview Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCardV2
              label="Vehicules actifs"
              value={brief.fleet_overview?.active_vehicles || 0}
              color="blue"
            />
            <StatCardV2
              label="Conducteurs dispo"
              value={brief.driver_availability?.available || 0}
              color="green"
            />
            <StatCardV2
              label="Absences"
              value={
                (brief.driver_availability?.unavailable_planned || 0) +
                (brief.driver_availability?.unavailable_unplanned || 0)
              }
              color="amber"
            />
            <StatCardV2
              label="Sinistres ouverts"
              value={brief.claims_overview?.open_claims || 0}
              color="purple"
            />
          </div>

          {/* Recommendations */}
          {brief.recommendations && brief.recommendations.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-500" />
                Recommandations du Jour
              </h3>
              <div className="space-y-3">
                {brief.recommendations.map((rec, idx) => {
                  const style = getPriorityStyle(rec.priority);
                  const RecIcon =
                    rec.priority === 'critical'
                      ? AlertCircle
                      : rec.priority === 'warning'
                        ? AlertTriangle
                        : Info;

                  return (
                    <div key={idx} className={`${style.bg} border ${style.border} rounded-lg p-4`}>
                      <div className="flex items-start gap-3">
                        <RecIcon className={`w-5 h-5 ${style.iconColor} flex-shrink-0 mt-0.5`} />
                        <div className="flex-1">
                          <p className={`font-medium ${style.text}`}>{rec.message}</p>

                          {/* Entity-specific deep links with action buttons */}
                          {rec.entities && rec.entities.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {rec.entities.map((entity, entityIdx) => {
                                const entityKey = `${entity.entity}-${entity.entityId}`;
                                const isHandling = handlingEntity === entityKey;
                                return (
                                  <div key={entityIdx} className="inline-flex items-center gap-1">
                                    <button
                                      onClick={() => handleActionClick(entity)}
                                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-l-md transition
                                                 ${style.text} bg-white border ${style.border} hover:bg-opacity-50`}
                                    >
                                      <ArrowRight className="w-4 h-4" />
                                      {entity.label}
                                    </button>
                                    {onMarkEntityHandled && (
                                      <button
                                        onClick={() => handleMarkHandled(entity, rec.category)}
                                        disabled={isHandling}
                                        className={`inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded-r-md transition
                                                   bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100
                                                   disabled:opacity-50 disabled:cursor-not-allowed`}
                                        title="Marquer comme traite (24h)"
                                      >
                                        {isHandling ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Check className="w-4 h-4" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Generic actions (secondary) */}
                          {rec.actions && rec.actions.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {rec.actions.map((action, actionIdx) => {
                                const ActionIcon = ACTION_ICONS[action.icon] || Eye;
                                return (
                                  <button
                                    key={actionIdx}
                                    onClick={() => handleActionClick(action)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition ${
                                      action.action === 'email_generate'
                                        ? 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-50'
                                        : 'bg-white/50 text-gray-600 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                  >
                                    <ActionIcon className="w-4 h-4" />
                                    {action.label}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Data Completeness */}
          {brief.data_completeness && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-emerald-500" />
                Completude des Donnees
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <CompletenessCard
                  label="Score global"
                  value={brief.data_completeness.overall_score}
                  color="emerald"
                />
                <CompletenessCard
                  label="Véhicules"
                  value={brief.data_completeness.vehicles_score}
                  color="blue"
                />
                <CompletenessCard
                  label="Conducteurs"
                  value={brief.data_completeness.drivers_score}
                  color="purple"
                />
              </div>
            </div>
          )}

          {/* Tasks Summary */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-blue-500" />
              Taches du Jour ({brief.work_items?.total || 0})
            </h3>
            <div className="grid grid-cols-4 gap-4">
              <TaskCountCard
                label="P0 Critique"
                value={brief.work_items?.by_priority?.P0?.length || 0}
                color="red"
              />
              <TaskCountCard
                label="P1 Important"
                value={brief.work_items?.by_priority?.P1?.length || 0}
                color="amber"
              />
              <TaskCountCard
                label="P2 Normal"
                value={brief.work_items?.by_priority?.P2?.length || 0}
                color="blue"
              />
              <TaskCountCard
                label="P3 Faible"
                value={brief.work_items?.by_priority?.P3?.length || 0}
                color="gray"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 bg-white flex items-center justify-between">
          <span className="text-sm text-slate-400 flex items-center gap-1">
            <Clock className="w-4 h-4" />
            Genere a{' '}
            {new Date(brief.generated_at).toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 transition text-sm font-medium"
          >
            Commencer la journee
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper components for Morning Brief
function StatCardV2({ label, value, color }) {
  const textColorMap = {
    blue: 'text-blue-600',
    green: 'text-emerald-600',
    amber: 'text-amber-600',
    purple: 'text-purple-600',
    red: 'text-red-600',
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
      <div className={`text-2xl font-bold ${textColorMap[color] || 'text-slate-800'}`}>{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

function CompletenessCard({ label, value, color }) {
  const barColorMap = {
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
      <div className="text-2xl font-bold text-slate-800">
        {value !== null ? `${value}%` : '-'}
      </div>
      <div className="text-sm text-slate-500">{label}</div>
      {value !== null && (
        <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColorMap[color] || 'bg-slate-500'} rounded-full`} style={{ width: `${value}%` }} />
        </div>
      )}
    </div>
  );
}

function TaskCountCard({ label, value, color }) {
  const textColorMap = {
    red: 'text-red-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
    gray: 'text-slate-600',
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
      <div className={`text-xl font-bold ${textColorMap[color] || 'text-slate-600'}`}>{value}</div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
    </div>
  );
}

// ============================================================================
// PROGRESS FOOTER
// ============================================================================

function ProgressFooter({ completed, total }) {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="sticky bottom-0 mt-6 -mx-6 px-6 py-3 bg-white border-t border-slate-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-slate-600">Progression</span>
        <div className="flex-1">
          <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
        <span className="text-sm font-bold text-slate-700">
          {completed}/{total} ({percentage}%)
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function PipelineHomeV2({ onNavigate }) {
  const { locale } = useFormat();
  const {
    sessionState,
    session,
    counts,
    currentDate,
    currentTime,
    schedule,
    loading,
    actionLoading,
    error,
    openDay,
    closeDay,
    refresh,
  } = usePipeline();

  const toast = useToast();

  // Local state
  const [workItems, setWorkItems] = useState([]);
  const [workItemsLoading, setWorkItemsLoading] = useState(false);
  const [dailyReport, setDailyReport] = useState(null);
  const [showBriefFlotte, setShowBriefFlotte] = useState(false);
  const [briefFlotte, setBriefFlotte] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);
  // Modal state for suggested actions
  const [quickEditModal, setQuickEditModal] = useState({ open: false, workItem: null, action: null });
  const [availabilityModal, setAvailabilityModal] = useState({ open: false, driver: null, workItemId: null });
  const [assignModal, setAssignModal] = useState({ open: false, driver: null, vehicles: [], workItemId: null });
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('pipeline_viewMode') || 'domains';
  });

  // Group items by domain
  const groupedItems = useMemo(() => {
    let filtered = workItems.filter(item => item.status === 'OPEN');

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.title?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query)
      );
    }

    // Apply priority filter
    if (priorityFilter && priorityFilter !== 'completed') {
      filtered = filtered.filter(item => item.priority === priorityFilter);
    }

    return groupItemsByDomain(filtered);
  }, [workItems, searchQuery, priorityFilter]);

  // Flat filtered items for list/timeline views
  const filteredItems = useMemo(() => {
    let filtered = workItems.filter(item => item.status === 'OPEN');
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.title?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query)
      );
    }
    if (priorityFilter && priorityFilter !== 'completed') {
      filtered = filtered.filter(item => item.priority === priorityFilter);
    }
    return filtered;
  }, [workItems, searchQuery, priorityFilter]);

  // Auto-expand domains with P0 items
  useEffect(() => {
    const domainsWithP0 = getDomainsWithP0(groupedItems);
    const newExpanded = {};
    domainsWithP0.forEach(d => {
      const domainKey = d.replace('domain:', '');
      newExpanded[domainKey] = true;
    });
    // Also expand first non-empty domain if nothing is expanded
    if (Object.keys(newExpanded).length === 0) {
      const firstNonEmpty = Object.entries(groupedItems).find(([, data]) => data.count > 0);
      if (firstNonEmpty) {
        newExpanded[firstNonEmpty[0]] = true;
      }
    }
    setExpandedDomains(newExpanded);
  }, [groupedItems]);

  // Fetch work items when session is open
  useEffect(() => {
    async function fetchWorkItems() {
      if (sessionState === 'SESSION_OPEN') {
        setWorkItemsLoading(true);
        try {
          const result = await pipelineAPI.getWorkItems();
          setWorkItems(result.data || []);
        } catch (err) {
          console.error('Failed to fetch work items:', err);
        } finally {
          setWorkItemsLoading(false);
        }
      }
    }
    fetchWorkItems();
  }, [sessionState]);

  // Fetch daily report when session is closed
  useEffect(() => {
    async function fetchDailyReport() {
      if (sessionState === 'SESSION_CLOSED' && currentDate) {
        try {
          const report = await pipelineAPI.getDailyReport(currentDate);
          setDailyReport(report);
        } catch (err) {
          console.error('Failed to fetch daily report:', err);
        }
      }
    }
    fetchDailyReport();
  }, [sessionState, currentDate]);

  // Handlers
  const handleOpenDay = async () => {
    try {
      await openDay();
      const result = await pipelineAPI.getWorkItems();
      setWorkItems(result.data || []);
    } catch (err) {
      console.error('Failed to open day:', err);
    }
  };

  const handleCloseDay = async () => {
    try {
      const result = await closeDay();
      setDailyReport(result);
    } catch (err) {
      console.error('Failed to close day:', err);
    }
  };

  const handleRefresh = async () => {
    try {
      await refresh();
      const result = await pipelineAPI.getWorkItems();
      setWorkItems(result.data || []);
    } catch (err) {
      console.error('Failed to refresh:', err);
    }
  };

  const handleShowBriefFlotte = async () => {
    setBriefLoading(true);
    try {
      const brief = await pipelineAPI.getBriefFlotte(currentDate);
      setBriefFlotte(brief);
      setShowBriefFlotte(true);
    } catch (err) {
      console.error('Failed to fetch Morning Brief:', err);
      toast?.error?.('Erreur lors du chargement du Morning Brief');
    } finally {
      setBriefLoading(false);
    }
  };

  // Handler for marking an entity as handled in Morning Brief
  const handleMarkEntityHandled = async (entity, category) => {
    try {
      await pipelineAPI.logRecommendationAction({
        entity_type: entity.entity,
        entity_id: entity.entityId,
        recommendation_category: category,
        action_type: 'acknowledged',
        action_note: 'Marked as handled from Morning Brief',
      });
      // Refresh the brief to show updated recommendations
      const brief = await pipelineAPI.getBriefFlotte(currentDate);
      setBriefFlotte(brief);
      toast?.success?.('Action enregistrée - masquée pour 24h');
    } catch (err) {
      console.error('Failed to mark entity as handled:', err);
      toast?.error?.("Erreur lors de l'enregistrement");
      throw err; // Re-throw so the UI can show error state
    }
  };

  const handleWorkItemAction = async (itemId, action, options) => {
    try {
      if (action && !['DONE', 'BLOCK', 'SNOOZE', 'DISMISS', 'START', 'REOPEN'].includes(action)) {
        const item = workItems.find(w => w.id === itemId);
        if (item && item.entity_kind === 'claim') {
          await claimAPI.workflowAction(item.entity_id, action);
        }
      } else {
        await pipelineAPI.workItemAction(itemId, action, options);
      }
      const result = await pipelineAPI.getWorkItems();
      setWorkItems(result.data || []);
    } catch (err) {
      console.error('Failed to perform action:', err);
    }
  };

  // Handler for opening the action logging modal
  const handleLogAction = item => {
    setSelectedWorkItem(item);
    setShowActionModal(true);
  };

  // Handler for when action is successfully logged
  const handleActionLogged = async () => {
    // Refresh work items to reflect any status changes
    const result = await pipelineAPI.getWorkItems();
    setWorkItems(result.data || []);
    setShowActionModal(false);
    setSelectedWorkItem(null);
  };

  // Handler for suggested actions that open modals
  const handleSuggestedActionModal = useCallback(async (workItem, action) => {
    const entityKind = action.entity || workItem.entity_kind;
    const entityId = action.entityId || workItem.entity_id;

    try {
      if (action.action === 'form_complete') {
        // Check if it's an availability update
        const fields = action.fields || [];
        if (fields.includes('availability_status') && entityKind === 'driver') {
          const res = await driverAPI.getById(entityId);
          const driver = res.data || res;
          setAvailabilityModal({ open: true, driver, workItemId: workItem.id });
          return;
        }
        // Generic field edit
        setQuickEditModal({ open: true, workItem, action });
      } else if (action.action === 'assign') {
        if (entityKind === 'vehicle') {
          // Fetch vehicle + all drivers for assignment modal
          const [vehRes, drvRes] = await Promise.all([
            vehicleAPI.getById(entityId),
            driverAPI.getAll(),
          ]);
          const vehicle = vehRes.data || vehRes;
          const drivers = (drvRes.data || drvRes) || [];
          // VehicleAssignmentToDriverModal expects driver + vehicles list
          // We need to pick a driver — open QuickEditModal as fallback for now
          // since the existing modal is designed from driver->vehicle, not vehicle->driver
          setQuickEditModal({
            open: true,
            workItem,
            action: { ...action, label: action.label || 'Assigner conducteur', fields: ['assigned_driver_id'] },
          });
        } else {
          setQuickEditModal({ open: true, workItem, action });
        }
      } else if (action.action === 'document_upload') {
        // Defer to navigate for now (scanner integration later)
        handleNavigateToEntity(entityKind, entityId);
      }
    } catch (err) {
      console.error('Failed to open modal for suggested action:', err);
      toast?.error?.('Erreur lors de l\'ouverture');
    }
  }, [toast]);

  // Handle modal success: mark work item DONE + refresh
  const handleModalSuccess = useCallback(async (workItemId) => {
    try {
      if (workItemId) {
        await pipelineAPI.workItemAction(workItemId, 'DONE');
      }
      const result = await pipelineAPI.getWorkItems();
      setWorkItems(result.data || []);
      toast?.success?.('Mise a jour enregistree, tache terminee');
    } catch (err) {
      console.error('Failed to complete work item after modal:', err);
      // Still refresh the list
      const result = await pipelineAPI.getWorkItems();
      setWorkItems(result.data || []);
    }
  }, [toast]);

  const handleNavigateToEntity = useCallback(
    (entityKind, entityId, spotlight = null) => {
      if (!onNavigate || !entityKind || !entityId) return;
      const viewMap = {
        vehicle: 'vehicles',
        driver: 'drivers',
        claim: 'claims',
        maintenance: 'maintenance',
        insurance_contract: 'insurance',
      };
      const view = viewMap[entityKind];
      if (view) {
        // Pass spotlight field for highlighting in detail view (feature tour style)
        onNavigate(view, {
          selectedId: entityId,
          returnTo: 'pipeline',
          spotlight, // e.g., 'insurance_expiry', 'license_expiry', 'assigned_driver'
        });
      }
    },
    [onNavigate]
  );

  const handleEmailGeneration = useCallback(
    async (item, templateCode) => {
      if (!item || !templateCode) return;
      try {
        const url = `${API_BASE_URL}/email_templates/${templateCode}/download/${item.entity_kind}/${item.entity_id}?workItemId=${item.id}`;
        const response = await fetch(url, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error("Erreur lors de la generation de l'email");

        const contentDisposition = response.headers.get('Content-Disposition');
        const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
        const filename = filenameMatch ? filenameMatch[1] : `${templateCode}_${item.entity_id}.eml`;

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.success(`Email pret: ${filename}`, 'Email genere');
      } catch (err) {
        console.error('Failed to generate email:', err);
        toast.error("Impossible de generer l'email. Veuillez reessayer.", 'Erreur');
      }
    },
    [toast]
  );

  const toggleDomain = domainKey => {
    setExpandedDomains(prev => ({ ...prev, [domainKey]: !prev[domainKey] }));
  };

  const handleViewModeChange = mode => {
    setViewMode(mode);
    localStorage.setItem('pipeline_viewMode', mode);
  };

  const goToDashboard = () => {
    if (onNavigate) onNavigate('dashboard');
  };

  // Calculate counts
  // Progress bar: initial_task_count = stock of remaining tasks when day was opened (constant)
  // done_today = items resolved during today's session (increases as user works)
  const completedToday = counts?.done_today || 0;
  const initialTaskCount = counts?.initial_task_count;
  // Denominator: use initial snapshot as ceiling; if new tasks appeared during day, grow to accommodate
  const remainingActive = workItems.filter(i => !['DISMISSED', 'SNOOZED'].includes(i.status)).length;
  const totalItems = initialTaskCount != null
    ? Math.max(initialTaskCount, remainingActive + completedToday)
    : remainingActive + completedToday;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Chargement du pipeline...</span>
      </div>
    );
  }

  // Serious mode = session open (pipeline active work mode)
  const isSeriousMode = sessionState === 'SESSION_OPEN';

  return (
    <div className={`min-h-screen ${isSeriousMode ? 'bg-orange-100/60' : ''}`} data-tour="pipeline-page">
    <div className="p-6 max-w-6xl mx-auto min-h-full">
      {/* Header */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6" data-tour="pipeline-header">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <ListChecks className="w-6 h-6 text-slate-400" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">Pipeline du Jour</h1>
                {isSeriousMode && (
                  <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-orange-500 text-white rounded">
                    Serious
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                {sessionState === 'SESSION_OPEN' ? (
                  <>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Session ouverte depuis{' '}
                      {session?.started_at
                        ? new Date(session.started_at).toLocaleTimeString(locale, {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : currentTime}
                    </span>
                    <span className="text-gray-300">|</span>
                    <span>{totalItems} taches actives</span>
                  </>
                ) : sessionState === 'SESSION_CLOSED' ? (
                  <span className="text-amber-600">Session terminee</span>
                ) : (
                  <span>Aucune session active</span>
                )}
              </div>
            </div>
          </div>

          {sessionState === 'SESSION_OPEN' && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleShowBriefFlotte}
                disabled={briefLoading}
                data-tour="pipeline-brief"
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-md
                          hover:bg-slate-900 transition text-sm font-medium"
              >
                {briefLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sun className="w-4 h-4" />
                )}
                Morning Brief
              </button>

              <button
                onClick={handleRefresh}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-md
                          hover:bg-slate-200 transition text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${actionLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Rafraichir</span>
              </button>

              <button
                onClick={handleCloseDay}
                disabled={actionLoading}
                data-tour="pipeline-close"
                className="flex items-center gap-2 px-4 py-2 text-red-600 border border-slate-200
                          rounded-md hover:bg-red-50 transition text-sm"
              >
                <StopCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Clore la journee</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-white border border-red-200 rounded-md flex items-center gap-2 text-red-600 text-sm">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* NO_SESSION State */}
      {sessionState === 'NO_SESSION' && (
        <div className="mt-8 text-center">
          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <ListChecks className="w-10 h-10 mx-auto text-slate-400 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Pret a commencer ?</h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto text-sm">
              Ouvrez votre journee de travail pour voir les taches prioritaires et suivre votre
              progression.
            </p>

            {schedule?.mode === 'fixed' && !schedule?.within_schedule && (
              <div className="mb-4 p-3 bg-white border border-slate-200 rounded-md inline-flex items-center gap-2 text-slate-600 text-sm">
                <Clock className="w-4 h-4" />
                <span>
                  Horaires : {schedule.day_start_time} - {schedule.day_end_time}
                </span>
              </div>
            )}

            <div className="flex justify-center gap-3">
              <button
                onClick={handleOpenDay}
                disabled={actionLoading || (schedule?.mode === 'fixed' && !schedule?.can_open)}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-md
                          hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium"
              >
                {actionLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Commencer la journee
              </button>

              <button
                onClick={goToDashboard}
                className="flex items-center gap-2 px-5 py-2.5 text-slate-600 hover:text-slate-800
                          transition text-sm"
              >
                <LayoutDashboard className="w-4 h-4" />
                Voir le dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SESSION_OPEN State */}
      {sessionState === 'SESSION_OPEN' && (
        <div>
          {/* Priority Counters */}
          <div data-tour="pipeline-counters">
            <PriorityCountersV2
              counts={{ ...counts, completed: completedToday }}
              onFilterByPriority={setPriorityFilter}
              activeFilter={priorityFilter}
            />
          </div>

          {/* Pending Emails Banner + Accordion */}
          <div data-tour="pipeline-emails">
            <PendingEmailsBanner
              items={workItems}
              onViewEmails={() => handleViewModeChange('emails')}
            />
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            {/* View Toggle */}
            <div className="flex items-center border border-slate-200 rounded-lg" data-tour="pipeline-views">
              {[
                { key: 'domains', label: 'Domaines', icon: Car },
                { key: 'list', label: 'Liste', icon: ListChecks },
                { key: 'timeline', label: 'Timeline', icon: Calendar },
                { key: 'emails', label: 'Emails', icon: Mail },
              ].map((tab, idx, arr) => {
                const TabIcon = tab.icon;
                const isActive = viewMode === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => handleViewModeChange(tab.key)}
                    className={`px-4 py-2 text-sm flex items-center gap-2 transition-colors
                      ${idx === 0 ? 'rounded-l-lg' : ''} ${idx === arr.length - 1 ? 'rounded-r-lg' : ''}
                      ${isActive ? 'bg-slate-800 text-white font-medium' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                  >
                    <TabIcon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 py-2 border border-slate-200 rounded-md w-64 text-sm focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Work Items */}
          {workItemsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Chargement des taches...</span>
            </div>
          ) : viewMode === 'list' ? (
            /* === LIST VIEW === */
            <WorkItemFlatList
              items={filteredItems}
              onAction={handleWorkItemAction}
              onNavigate={handleNavigateToEntity}
              onEmail={handleEmailGeneration}
              onSuggestedAction={handleSuggestedActionModal}
            />
          ) : viewMode === 'timeline' ? (
            /* === TIMELINE VIEW === */
            <WorkItemTimeline
              items={filteredItems}
              onAction={handleWorkItemAction}
              onNavigate={handleNavigateToEntity}
              onEmail={handleEmailGeneration}
              onSuggestedAction={handleSuggestedActionModal}
            />
          ) : viewMode === 'emails' ? (
            /* === EMAILS VIEW === */
            <PendingEmailsAccordion
              items={workItems.filter(i => i.status === 'OPEN')}
              onEmail={handleEmailGeneration}
            />
          ) : (
            /* === DOMAINS VIEW (default) === */
            <div className="space-y-2">
              {Object.entries(groupedItems).map(([domainKey, data]) => {
                const items = Object.values(data.byType).flat();
                if (items.length === 0) return null;

                return (
                  <DomainSection
                    key={domainKey}
                    domain={data}
                    domainKey={domainKey}
                    items={items}
                    isExpanded={expandedDomains[domainKey]}
                    onToggle={() => toggleDomain(domainKey)}
                    onAction={handleWorkItemAction}
                    onNavigate={handleNavigateToEntity}
                    onEmail={handleEmailGeneration}
                    onLogAction={handleLogAction}
                    onSuggestedAction={handleSuggestedActionModal}
                  />
                );
              })}

              {Object.values(groupedItems).every(d => d.count === 0) && (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                  <CheckCircle className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
                  <h3 className="text-lg font-medium text-slate-800">Aucune tache en attente</h3>
                  <p className="text-slate-500 mt-1 text-sm">
                    {searchQuery || priorityFilter
                      ? 'Aucun resultat pour les filtres actifs'
                      : 'Toutes les taches ont ete traitees!'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Progress Footer */}
          <ProgressFooter completed={completedToday} total={totalItems} />
        </div>
      )}

      {/* SESSION_CLOSED State */}
      {sessionState === 'SESSION_CLOSED' && (
        <div className="mt-8">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <CheckCircle className="w-10 h-10 mx-auto text-emerald-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Journee terminee</h2>
            <p className="text-slate-500 mb-6 text-sm">Votre session de travail est cloturee.</p>

            {dailyReport && (
              <div className="border border-slate-200 rounded-md p-4 mb-6 max-w-md mx-auto">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Taches completees</span>
                    <p className="text-xl font-bold text-emerald-600">
                      {dailyReport.completed_count || 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Taches reportees</span>
                    <p className="text-xl font-bold text-amber-600">
                      {dailyReport.snoozed_count || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-3">
              <button
                onClick={handleShowBriefFlotte}
                disabled={briefLoading}
                className="flex items-center gap-2 px-5 py-2.5 text-slate-600 hover:text-slate-800
                          transition text-sm"
              >
                {briefLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                Voir le Morning Brief
              </button>

              <button
                onClick={goToDashboard}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-white rounded-md
                          hover:bg-slate-900 transition text-sm font-medium"
              >
                <LayoutDashboard className="w-4 h-4" />
                Aller au dashboard
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Morning Brief Modal */}
      <MorningBriefModalV2
        isOpen={showBriefFlotte}
        onClose={() => setShowBriefFlotte(false)}
        brief={briefFlotte}
        onNavigate={view => {
          if (onNavigate) onNavigate(view);
        }}
        onNavigateToEntity={handleNavigateToEntity}
        onEmail={handleEmailGeneration}
        onMarkEntityHandled={handleMarkEntityHandled}
      />

      {/* Work Item Action Modal */}
      <WorkItemActionModal
        isOpen={showActionModal}
        onClose={() => {
          setShowActionModal(false);
          setSelectedWorkItem(null);
        }}
        workItem={selectedWorkItem}
        onSuccess={handleActionLogged}
        onMessage={msg => toast?.[msg.type]?.(msg.message)}
      />

      {/* Pipeline Quick Edit Modal */}
      <PipelineQuickEditModal
        isOpen={quickEditModal.open}
        onClose={() => setQuickEditModal({ open: false, workItem: null, action: null })}
        workItem={quickEditModal.workItem}
        action={quickEditModal.action}
        onSuccess={() => handleModalSuccess(quickEditModal.workItem?.id)}
      />

      {/* Driver Availability Modal (from pipeline) */}
      <DriverAvailabilityModal
        isOpen={availabilityModal.open}
        onClose={() => setAvailabilityModal({ open: false, driver: null, workItemId: null })}
        driver={availabilityModal.driver}
        onSuccess={() => {
          setAvailabilityModal({ open: false, driver: null, workItemId: null });
          handleModalSuccess(availabilityModal.workItemId);
        }}
        onMessage={msg => toast?.[msg.type]?.(msg.message)}
      />
    </div>
    </div>
  );
}

export default PipelineHomeV2;
